import React, { useEffect, useState, useCallback } from 'react';
import { cursosService } from '../services/api';
import HtmlContent, { stripHtml } from '../components/HtmlContent';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import { CURSOS_DISPONIBLES_PAGE_SIZE } from '../constants/uploadLimits';
import { notifyCursosDisponiblesChanged } from '../hooks/useHayCursosDisponibles';
import { formatDuracionClase } from '../utils/duration';
import './Dashboard.css';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MAX_DESC_PREVIEW = 240; // aproximación a ~5 líneas para decidir si mostrar "Ver más"

const formatHora = (horaStr) => {
  if (!horaStr) return '-';
  if (typeof horaStr === 'string' && horaStr.length >= 5) {
    return horaStr.substring(0, 5);
  }
  return horaStr;
};

/** Fecha de calendario sin desfase UTC (YYYY-MM-DD → día local). */
const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    return new Date(dateString).toLocaleDateString('es-AR');
  }
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR');
};

const CursosDisponibles = () => {
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(true);
  const [expandidoId, setExpandidoId] = useState(null);
  const [inscribiendoId, setInscribiendoId] = useState(null);
  const [mensajeInscripcion, setMensajeInscripcion] = useState({ tipo: '', texto: '' });
  const [cursoDescripcionModal, setCursoDescripcionModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCursos, setTotalCursos] = useState(0);

  const loadCursos = useCallback((page) => {
    setLoadingCursos(true);
    cursosService
      .getCursosDisponibles({ page, page_size: CURSOS_DISPONIBLES_PAGE_SIZE })
      .then(({ results, total }) => {
        setCursosDisponibles(results);
        setTotalCursos(total);
        notifyCursosDisponiblesChanged();
      })
      .catch(() => {
        setCursosDisponibles([]);
        setTotalCursos(0);
        notifyCursosDisponiblesChanged();
      })
      .finally(() => setLoadingCursos(false));
  }, []);

  useEffect(() => {
    loadCursos(currentPage);
  }, [currentPage, loadCursos]);

  const toggleVerMas = (id) => {
    setExpandidoId(prev => prev === id ? null : id);
  };

  const handleInscribirme = async (cursoId) => {
    setMensajeInscripcion({ tipo: '', texto: '' });
    setInscribiendoId(cursoId);
    try {
      await cursosService.preInscribirseEnCurso(cursoId);
      setMensajeInscripcion({
        tipo: 'ok',
        texto: 'Te has pre-inscripto exitosamente. La pre-inscripción no garantiza lugar en el curso.',
      });
      loadCursos(currentPage);
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo completar la pre-inscripción.';
      setMensajeInscripcion({ tipo: 'error', texto: msg });
    } finally {
      setInscribiendoId(null);
    }
  };

  return (
    <>
      <div className="dashboard-header">
        <h1>Cursos disponibles</h1>
        <p>Cursos con inscripción abierta en los que puedes inscribirte</p>
      </div>

      <section className="dashboard-section">
        <h2 className="dashboard-section-title">Cursos con inscripción abierta</h2>
        {loadingCursos && cursosDisponibles.length === 0 ? (
          <div className="dashboard-loading-cursos">
            <div className="loading-spinner"></div>
            <p>Cargando cursos...</p>
          </div>
        ) : cursosDisponibles.length === 0 ? (
          <p className="dashboard-sin-cursos">No hay cursos con inscripción abierta en este momento.</p>
        ) : (
          <>
            {mensajeInscripcion.texto && mensajeInscripcion.tipo === 'ok' && (
              <div className="dashboard-inscripcion-msg dashboard-inscripcion-ok">
                {mensajeInscripcion.texto}
              </div>
            )}
            <ConfirmPopup
              open={Boolean(mensajeInscripcion.texto) && mensajeInscripcion.tipo === 'error'}
              title="Error"
              message={mensajeInscripcion.texto}
              confirmLabel="Aceptar"
              cancelLabel={null}
              onConfirm={() => setMensajeInscripcion({ tipo: '', texto: '' })}
              onCancel={() => setMensajeInscripcion({ tipo: '', texto: '' })}
              variant="danger"
            />
            <div className={`dashboard-cursos-grid ${loadingCursos ? 'pagination-content-loading' : ''}`}>
              {cursosDisponibles.map((curso) => {
                const nivelNombre = curso.nivel_nombre || (curso.nivel && typeof curso.nivel === 'object' && curso.nivel.nombre ? curso.nivel.nombre : null);
                const descPlain = stripHtml(curso.descripcion);
                const descEsLarga = descPlain.length > MAX_DESC_PREVIEW;
                return (
                <div key={curso.id} className="dashboard-curso-card">
                  {nivelNombre && (
                    <span className="dashboard-curso-badge-nivel" aria-label={`Nivel: ${nivelNombre}`}>
                      {nivelNombre}
                    </span>
                  )}
                  <div className="dashboard-curso-card-header">
                    <h3>{curso.nombre}</h3>
                  </div>

                  {curso.descripcion && (
                    <HtmlContent content={curso.descripcion} tag="div" className="dashboard-curso-desc" />
                  )}
                  <div className="dashboard-curso-actions">
                    {descEsLarga && (
                      <button
                        type="button"
                        className="dashboard-curso-vermas"
                        onClick={() => setCursoDescripcionModal(curso)}
                      >
                        Ver más
                      </button>
                    )}
                    <button
                      type="button"
                      className="dashboard-curso-vermas"
                      onClick={() => toggleVerMas(curso.id)}
                      aria-expanded={expandidoId === curso.id}
                    >
                      {expandidoId === curso.id ? 'Ocultar clases' : 'Ver clases'}
                    </button>
                    {curso.estoy_inscripto ? (
                      <span className="dashboard-curso-ya-inscripto">Ya inscripto</span>
                    ) : curso.estoy_preinscripto ? (
                      <span className="dashboard-curso-preinscripto">Pre-inscripto (pendiente)</span>
                    ) : (
                      <button
                        type="button"
                        className="dashboard-curso-inscribirme"
                        onClick={() => handleInscribirme(curso.id)}
                        disabled={inscribiendoId === curso.id}
                      >
                        {inscribiendoId === curso.id ? 'Inscribiendo...' : 'Inscribirme'}
                      </button>
                    )}
                  </div>
                  {expandidoId === curso.id && (
                    <div className="dashboard-curso-detalle">
                      <div className="dashboard-curso-dias">
                        <strong>Día(s) de clase:</strong>{' '}
                        {(curso.dias_semana && curso.dias_semana.length > 0)
                          ? curso.dias_semana.map(d => DIAS_SEMANA[d]).join(', ')
                          : 'No definido'}
                      </div>
                      <div className="dashboard-curso-horario">
                        <strong>Horario:</strong> {formatHora(curso.hora_inicio_clase)}
                        {(() => {
                          const d = formatDuracionClase(curso.duracion_clase);
                          return d ? ` (duración ${d})` : '';
                        })()}
                      </div>
                      {curso.fecha_inicio && curso.fecha_fin && (
                        <div className="dashboard-curso-fechas">
                          <strong>Período:</strong> {formatDateOnly(curso.fecha_inicio)} – {formatDateOnly(curso.fecha_fin)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
            {totalCursos > CURSOS_DISPONIBLES_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCursos / CURSOS_DISPONIBLES_PAGE_SIZE)}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </section>

      {cursoDescripcionModal && (
        <div
          className="dashboard-curso-desc-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-curso-desc-modal-title"
        >
          <div className="dashboard-curso-desc-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-curso-desc-modal-header">
              <h3 id="dashboard-curso-desc-modal-title">
                Descripción de: {cursoDescripcionModal.nombre}
              </h3>
              <button
                type="button"
                className="dashboard-curso-desc-modal-close"
                onClick={() => setCursoDescripcionModal(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="dashboard-curso-desc-modal-body">
              {cursoDescripcionModal.descripcion && (
                <HtmlContent content={cursoDescripcionModal.descripcion} tag="div" className="dashboard-curso-desc" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CursosDisponibles;
